import { useMemo } from 'react';
import { CATALOGUE, getActiveSources, isRenderable } from '../data/catalogue';
import { LEGEND_METADATA, FLOOD_RISK_LEGEND, FLOOD_TYPE_LEGEND_SCOTLAND, FLOOD_TYPE_LEGEND_WALES } from '../data/legend-metadata';
import { buildSoilLegend } from './soilLegend';

// Categories rendered as ONE legend card with multiple swatches (no per-source
// title). 'protected-sites' bundles the conservation designations (SSSI/SAC/SPA)
// with Scotland's NVZ into a single card (BBB-456): England shows 3 swatches
// (designations only), Scotland 4 (3 designations + NVZ) and Wales 3. Flood is
// handled separately below as one unified High/Medium/Low risk key.
//
// England's count changed when its NVZ and safeguard zones moved out to the
// 'nitrate-water' category, because their fills cover too much of the country to
// share a toggle. The counts here are the ones catalogue.test.js asserts; read
// them from there rather than from this comment if the two ever disagree again.
// Cards built from several sources rather than one. `hideLabel` drops the card
// heading: 'nitrate-water' carries two designations whose swatch labels already
// name them, so a heading above them only repeats the toggle.
//
// 'Protected sites', decided 11/09/2026: the card holds conservation
// designations only, since England's NVZ and safeguard zones moved out to
// 'nitrate-water', so "Regulatory areas" had stopped being true. The toggle and
// this card used to carry different names for the same layer.
const MERGED_CARDS = {
  'protected-sites': { label: 'Protected sites' },
  'nitrate-water': { label: 'Nitrate and water zones', hideLabel: true },
};

// BBB-525 / 023 cluster 3: LEGEND_METADATA has carried a `sourceLink` for all
// eleven entries since BBB-457 and NOTHING read it, so eleven source URLs were
// in the bundle and invisible. Soil was the only category with a source link in
// the map legend, and it built its own.
//
// Wording confirmed by Davin 11/09/2026: "Learn more about <thing>", the phrasing
// from his 03/09 braindump. Six of the eleven links name a designation, which is
// a proper noun and reads correctly mid-sentence ("Learn more about Nitrate
// Vulnerable Zones"). The other five carry an explicit lower-case
// `sourceLinkLabel`: three name a scheme in our own words, where a capital
// mid-sentence reads wrong ("Learn more about land classification"), and two
// point at a nation's protected-sites hub rather than a single designation
// ("Learn more about protected sites in Wales").
//
// Destinations reviewed one by one on 11/09/2026 and settled: two re-pointed,
// England's ALC kept as the least bad option, the rest already fine. They are
// pinned in data/legend-metadata.test.js.
const sourceLinkText = (entry) =>
  `Learn more about ${entry.sourceLinkLabel ?? entry.label}`;

/**
 * The source links for one legend card, deduplicated by URL.
 *
 * Merged cards draw on several sources, so a card can carry several links: the
 * England 'Protected sites' card merges SSSI, SAC and SPA, each with its own
 * page. Deduplicated because two sources in one card can share a destination.
 *
 * @param {Array<object>} entries LEGEND_METADATA entries contributing to the card
 * @returns {Array<{href: string, text: string}>}
 */
function sourceLinksFor(entries) {
  const byHref = new Map();
  for (const entry of entries) {
    if (!entry?.sourceLink || byHref.has(entry.sourceLink)) continue;
    byHref.set(entry.sourceLink, {
      href: entry.sourceLink,
      text: sourceLinkText(entry),
    });
  }
  return [...byHref.values()];
}

function selectLegendSources(sources) {
  const selectedByGroup = new Map();

  for (const source of sources) {
    const group = source.legendGroup ?? source.id;
    const selected = selectedByGroup.get(group);
    const sourcePrecedence = source.precedence ?? 100;
    const selectedPrecedence = selected?.precedence ?? 100;

    if (!selected || sourcePrecedence >= selectedPrecedence) {
      selectedByGroup.set(group, source);
    }
  }

  return sources.filter((source) => selectedByGroup.get(source.legendGroup ?? source.id) === source);
}

function normaliseRegion(viewportRegion) {
  return viewportRegion === 'england' ||
    viewportRegion === 'scotland' ||
    viewportRegion === 'wales'
    ? viewportRegion
    : null;
}

export function useLegend(layerStates, viewportRegion, designationPresence, zoneSoilData, zoneSoilStatus) {
  return useMemo(() => {
    const region = normaliseRegion(viewportRegion);
    const items = [];

    for (const category of CATALOGUE) {
      const state = layerStates[category.id];
      if (!state?.visible) continue;

      // Soil is the one category whose key is driven by the open field rather
      // than by the catalogue: the providers publish 29 classes for England and
      // Wales and 11 for Scotland, and a farmer's field holds a handful. See
      // lib/soilLegend.js.
      if (category.id === 'soil') {
        items.push(buildSoilLegend(zoneSoilData, region, zoneSoilStatus));
        continue;
      }

      if (!region) continue;

      // Flood key varies by nation to match what each source actually renders:
      // England = High/Medium/Low risk ramp; Scotland = flood type (SEPA medium
      // tiles); Wales = flood type per NRW's three layers + a "darker = higher
      // risk" caption (its ramps can't be recoloured). (BBB-457)
      if (category.id === 'flood') {
        const hasFlood = getActiveSources(category, region).filter(isRenderable).length > 0;
        if (hasFlood) {
          const floodLegend =
            region === 'scotland'
              ? FLOOD_TYPE_LEGEND_SCOTLAND
              : region === 'wales'
                ? FLOOD_TYPE_LEGEND_WALES
                : FLOOD_RISK_LEGEND;
          items.push({ sourceId: 'flood', ...floodLegend });
        }
        continue;
      }

      const renderableSources = getActiveSources(category, region).filter(isRenderable);
      const selected = selectLegendSources(renderableSources);

      const merged = MERGED_CARDS[category.id];
      if (merged) {
        const swatches = [];
        const contributing = [];
        for (const source of selected) {
          if (
            category.id === 'protected-sites' &&
            designationPresence &&
            designationPresence[source.id] === false
          ) {
            continue;
          }
          const entry =
            LEGEND_METADATA[source.legendGroup] || LEGEND_METADATA[source.id];
          if (entry?.swatches) {
            swatches.push(...entry.swatches);
            contributing.push(entry);
          }
        }
        if (swatches.length) {
          items.push({
            sourceId: category.id,
            label: merged.label,
            hideLabel: merged.hideLabel,
            swatches,
            sourceLinks: sourceLinksFor(contributing),
          });
        }
        continue;
      }

      for (const source of selected) {
        const entry = LEGEND_METADATA[source.id];
        if (entry) {
          items.push({
            sourceId: source.id,
            ...entry,
            sourceLinks: sourceLinksFor([entry]),
          });
        }
      }
    }

    return items;
  }, [layerStates, viewportRegion, designationPresence, zoneSoilData, zoneSoilStatus]);
}
